import React, { useEffect, useRef, useState } from 'react';
import { RouteComponentProps } from 'react-router';
import { Box, Grid } from '@material-ui/core';
import {
    AvailablePropertyFragment,
    PropertyType,
    useAvailablePropertiesQuery
} from 'src/generated/graphql';
import Page from 'src/components/Page';
import { getMaxDesks, getMinCost, getMinDesks } from 'src/libs/utils/property';
import {
    getMaxSizeAvailableProperties,
    getMinLeaseCost,
    getMinSizeAvailableProperties
} from '../AvailableProperties/lib';
import { useStyles } from './styles';
import { Properties } from './components/Properties';

export type HomepageAvailableProperties = {
    officeNames: string;
}

export type AvailablePropertyFragmentExtra = AvailablePropertyFragment & {
    maxDesks: number;
    minDesks: number;
    minCost: number;
    maxAvailableSize: number;
    minAvailableSize: number;
}

interface TopAvailablePropertiesProps extends RouteComponentProps<{ propertyId: string }> {}

const TopAvailableProperties = ({ match }: TopAvailablePropertiesProps) => {
    const pageRef = useRef<HTMLDivElement | null>(null);
    const [properties, setProperties] = useState<AvailablePropertyFragmentExtra[]>();

    const classes = useStyles();
    const { data, previousData, error } = useAvailablePropertiesQuery({
        fetchPolicy: 'cache-and-network',
        context: {
            headers: {
                brokeroverride: 'DIRECTACCESS',
            },
        },
        variables: {
            filter: {}
        },
    });
    const availableProperties = data?.availableProperties || previousData?.availableProperties;

    useEffect(() => {
        if (availableProperties) {
            const shownProperties = availableProperties.filter((availProp) => (
                match.params.propertyId?.includes(availProp.id)
            ));
            setProperties(
                shownProperties.map(({ office, propertyType, ...restProperty }) => {
                    const maxDesks = getMaxDesks(office);
                    const minDesks = getMinDesks(office);
                    const minCost = propertyType === PropertyType.Lease && office.some(({ price }) => price)
                        ? getMinLeaseCost(office)
                        : getMinCost(office);
                    const maxAvailableSize = getMaxSizeAvailableProperties(office);
                    const minAvailableSize = getMinSizeAvailableProperties(office);

                    return {
                        ...restProperty,
                        office,
                        propertyType,
                        maxDesks: maxDesks || 0,
                        minDesks: minDesks || 0,
                        minCost: minCost || 0,
                        maxAvailableSize: maxAvailableSize || 0,
                        minAvailableSize: minAvailableSize || 0
                    };
                })
            );
        }
    }, [availableProperties]);

    if (error || !properties || !availableProperties) {
        return (<div />);
    }

    return (
        <Page title="Available properties">
            {properties && (
                <Grid item ref={pageRef} className={classes.container}>
                    <Box style={{ width: '100%' }}>
                        <Properties
                            properties={properties[0]}
                        />
                    </Box>
                </Grid>
            )}
        </Page>
    );
};

export default TopAvailableProperties;
